import { config } from "../../../config";
import { AvailableModel, countMessageTokens } from "../../../services/claude";
import { REQUEST_CANCELED_ERROR } from "../constants/errors";
import { ChatActions, ChatState, ContextDataItem, DataChunk } from "../stores/chatTypes";
import { getEnlightenMeSystemPrompt } from "../utils/ragchatStorage";
import { clearAllSavedExtractedData } from "./extractedDataHandler";

const EMPTY_COUNT = 0;
const MIN_CHUNK_ITEMS = 1;
const MIN_ITEMS_FOR_POP = 1;
const LAST_INDEX_OFFSET = 1;
const EXTRA_CHUNK_START = -1;
const EXTRA_CHUNK_END = -1;
const DEFAULT_TOKENS_PER_CONTEXT = 1000;
const TOKENS_PER_WORD = 1.3;
const MIN_QUERY_TOKEN_ESTIMATE = 5;
const MAX_QUERY_TOKEN_ESTIMATE = 500;
const FALLBACK_PROMPT_TOKENS = 500;
const MIN_TOKENS_PER_CONTEXT = 50;

interface ChunkParams {
  data: ContextDataItem[];
  totalCalculatedTokens: number;
  singleContextTokenLength: number;
  maxInputTokens: number;
  actualPromptTokens: number;
  systemPrompt: string;
  model?: AvailableModel;
  query?: string;
  abortSignal?: AbortSignal;
}

interface TokenParams {
  model?: AvailableModel;
  abortSignal?: AbortSignal;
}

interface AdjustChunkSizeParams {
  originalChunk: ContextDataItem[];
  query: string | undefined;
  systemPrompt: string;
  tokenParams: TokenParams;
}

interface AdjustChunkSizeResult {
  validChunk: ContextDataItem[];
  extraItems: ContextDataItem[];
  actualTokens: number;
}

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw new Error(REQUEST_CANCELED_ERROR);
  }
};

interface CountTokensArgs {
  context: ContextDataItem[];
  query?: string;
  systemPrompt: string;
  model?: AvailableModel;
  errorMessagePrefix?: string;
}

const countOrThrow = async (args: CountTokensArgs): Promise<number> => {
  const { context, query, systemPrompt, model, errorMessagePrefix = "Failed to count tokens" } = args;
  const result = await countMessageTokens({ context, query, systemPrompt, model });
  if (result.error || !result.tokenCount) {
    throw new Error(`${errorMessagePrefix}: ${result.error ?? "No token count returned"}`);
  }
  return result.tokenCount;
};

interface ShrinkState {
  chunk: ContextDataItem[];
  extraItems: ContextDataItem[];
}

const popToExtras = (state: ShrinkState): ShrinkState => {
  const chunk = [...state.chunk];
  const removed = chunk.pop();
  return {
    chunk,
    extraItems: removed ? [removed, ...state.extraItems] : state.extraItems
  };
};

const shrinkUntilFits = async (
  state: ShrinkState,
  params: AdjustChunkSizeParams
): Promise<AdjustChunkSizeResult> => {
  const { query, systemPrompt, tokenParams } = params;
  const maxTokens = config.maxInputTokens;

  if (state.chunk.length === EMPTY_COUNT) {
    return { validChunk: [], extraItems: params.originalChunk, actualTokens: EMPTY_COUNT };
  }

  throwIfAborted(tokenParams.abortSignal);

  const currentTokens = await countOrThrow({
    context: state.chunk,
    query,
    systemPrompt,
    model: tokenParams.model
  });

  if (currentTokens <= maxTokens) {
    return { validChunk: state.chunk, extraItems: state.extraItems, actualTokens: currentTokens };
  }

  return shrinkUntilFits(popToExtras(state), params);
};

const adjustChunkSize = async (params: AdjustChunkSizeParams): Promise<AdjustChunkSizeResult> => {
  throwIfAborted(params.tokenParams.abortSignal);

  const initial: ShrinkState = params.originalChunk.length > MIN_ITEMS_FOR_POP
    ? popToExtras({ chunk: params.originalChunk, extraItems: [] })
    : { chunk: [...params.originalChunk], extraItems: [] };

  return shrinkUntilFits(initial, params);
};

interface CalculateChunkSizesParams {
  totalItems: number;
  totalTokens: number;
  maxTokensPerChunk: number;
  singleContextTokenLength: number;
  actualPromptTokens: number;
}

const calculateInitialChunkSizes = (params: CalculateChunkSizesParams): number[] => {
  const { totalItems, totalTokens, maxTokensPerChunk, singleContextTokenLength, actualPromptTokens } = params;

  if (totalTokens <= maxTokensPerChunk) {
    return [totalItems];
  }

  const availableTokensPerChunk = maxTokensPerChunk - actualPromptTokens;
  console.log(`Using actual prompt tokens: ${actualPromptTokens} for chunk calculation`);

  const conservativeItemsPerChunk = Math.floor(availableTokensPerChunk / singleContextTokenLength);
  const safeItemsPerChunk = Math.max(conservativeItemsPerChunk, MIN_CHUNK_ITEMS);

  const estimatedChunks = Math.ceil(totalItems / safeItemsPerChunk);
  const actualItemsPerChunk = Math.floor(totalItems / estimatedChunks);
  const remainder = totalItems % estimatedChunks;

  return Array.from({ length: estimatedChunks }, (_unused, i) =>
    actualItemsPerChunk + (i < remainder ? MIN_CHUNK_ITEMS : EMPTY_COUNT)
  );
};

interface AssembleChunkParams {
  chunkData: ContextDataItem[];
  chunkSize: number;
  currentIndex: number;
  estimatedTokens: number;
  actualTokens: number;
}

const buildDataChunk = (params: AssembleChunkParams): DataChunk => ({
  data: params.chunkData,
  startIndex: params.currentIndex,
  endIndex: params.currentIndex + params.chunkSize - LAST_INDEX_OFFSET,
  estimatedTokens: params.estimatedTokens,
  actualTokens: params.actualTokens
});

interface ProcessChunkParams {
  chunkData: ContextDataItem[];
  chunkSize: number;
  currentIndex: number;
  estimatedTokens: number;
  query: string | undefined;
  systemPrompt: string;
  tokenParams: TokenParams;
}

interface ProcessChunkOutcome {
  chunks: DataChunk[];
  extras: ContextDataItem[];
}

const processOneChunk = async (params: ProcessChunkParams): Promise<ProcessChunkOutcome> => {
  const { chunkData, chunkSize, currentIndex, estimatedTokens, query, systemPrompt, tokenParams } = params;
  const actualTokens = await countOrThrow({ context: chunkData, query, systemPrompt, model: tokenParams.model });
  const maxTokens = config.maxInputTokens;

  if (actualTokens <= maxTokens) {
    return {
      chunks: [buildDataChunk({ chunkData, chunkSize, currentIndex, estimatedTokens, actualTokens })],
      extras: []
    };
  }

  const adjustment = await adjustChunkSize({
    originalChunk: chunkData,
    query,
    systemPrompt,
    tokenParams
  });

  const chunks: DataChunk[] = adjustment.validChunk.length > EMPTY_COUNT
    ? [buildDataChunk({
      chunkData: adjustment.validChunk,
      chunkSize: adjustment.validChunk.length,
      currentIndex,
      estimatedTokens,
      actualTokens: adjustment.actualTokens
    })]
    : [];

  return { chunks, extras: adjustment.extraItems };
};

interface WalkAccumulator {
  chunks: DataChunk[];
  extraItems: ContextDataItem[];
  currentIndex: number;
}

interface WalkContext {
  data: ContextDataItem[];
  chunkSizes: number[];
  singleContextTokenLength: number;
  query: string | undefined;
  systemPrompt: string;
  tokenParams: TokenParams;
}

const walkChunks = async (context: WalkContext): Promise<WalkAccumulator> => {
  const { data, chunkSizes, singleContextTokenLength, query, systemPrompt, tokenParams } = context;
  const initial: WalkAccumulator = { chunks: [], extraItems: [], currentIndex: EMPTY_COUNT };

  return chunkSizes.reduce<Promise<WalkAccumulator>>(
    async (accPromise, chunkSize) => {
      const acc = await accPromise;
      throwIfAborted(tokenParams.abortSignal);

      const chunkData = data.slice(acc.currentIndex, acc.currentIndex + chunkSize);
      if (chunkData.length === EMPTY_COUNT) {
        return acc;
      }

      const estimatedTokens = chunkSize * (singleContextTokenLength || DEFAULT_TOKENS_PER_CONTEXT);
      const outcome = await processOneChunk({
        chunkData,
        chunkSize,
        currentIndex: acc.currentIndex,
        estimatedTokens,
        query,
        systemPrompt,
        tokenParams
      });

      return {
        chunks: [...acc.chunks, ...outcome.chunks],
        extraItems: [...acc.extraItems, ...outcome.extras],
        currentIndex: acc.currentIndex + chunkSize
      };
    },
    Promise.resolve(initial)
  );
};

interface AppendExtrasParams {
  extraItems: ContextDataItem[];
  chunks: DataChunk[];
  singleContextTokenLength: number;
  chunkParams: ChunkParams;
}

const appendExtras = async (params: AppendExtrasParams): Promise<DataChunk[]> => {
  const { extraItems, chunks, singleContextTokenLength, chunkParams } = params;
  if (extraItems.length === EMPTY_COUNT) {
    return chunks;
  }

  const { query, systemPrompt, model } = chunkParams;
  const extraTokens = await countOrThrow({
    context: extraItems,
    query,
    systemPrompt,
    model,
    errorMessagePrefix: "Failed to count tokens for extra items"
  });

  if (extraTokens <= config.maxInputTokens) {
    return [
      ...chunks,
      {
        data: extraItems,
        startIndex: EXTRA_CHUNK_START,
        endIndex: EXTRA_CHUNK_END,
        estimatedTokens: extraItems.length * (singleContextTokenLength || DEFAULT_TOKENS_PER_CONTEXT),
        actualTokens: extraTokens
      }
    ];
  }

  const extraChunkedResult = await chunkDataByTokens({
    ...chunkParams,
    data: extraItems,
    totalCalculatedTokens: extraTokens
  });
  return [...chunks, ...extraChunkedResult];
};

export const chunkDataByTokens = async (params: ChunkParams): Promise<DataChunk[]> => {
  const { data, totalCalculatedTokens, singleContextTokenLength, maxInputTokens, actualPromptTokens, systemPrompt, model, query, abortSignal } = params;

  if (!data || data.length === EMPTY_COUNT) {
    return [];
  }

  throwIfAborted(abortSignal);

  const tokenParams: TokenParams = { model, abortSignal };
  const chunkSizes = calculateInitialChunkSizes({
    totalItems: data.length,
    totalTokens: totalCalculatedTokens,
    maxTokensPerChunk: maxInputTokens,
    singleContextTokenLength,
    actualPromptTokens
  });

  const walked = await walkChunks({
    data,
    chunkSizes,
    singleContextTokenLength,
    query,
    systemPrompt,
    tokenParams
  });

  return appendExtras({
    extraItems: walked.extraItems,
    chunks: walked.chunks,
    singleContextTokenLength,
    chunkParams: params
  });
};

export const estimateQueryTokens = (query: string): number => {
  if (!query || query.trim().length === EMPTY_COUNT) {
    return EMPTY_COUNT;
  }

  const words = query.trim().split(/\s+/);
  const estimatedTokens = Math.ceil(words.length * TOKENS_PER_WORD);

  return Math.min(Math.max(estimatedTokens, MIN_QUERY_TOKEN_ESTIMATE), MAX_QUERY_TOKEN_ESTIMATE);
};

interface CalculatePromptTokensParams {
  systemPrompt: string;
  model?: AvailableModel;
  abortSignal?: AbortSignal;
}

export const calculateActualPromptTokens = async (params: CalculatePromptTokensParams): Promise<number> => {
  const { systemPrompt, model, abortSignal } = params;

  throwIfAborted(abortSignal);

  const result = await countMessageTokens({
    context: [],
    query: "",
    systemPrompt,
    model
  });

  if (result.error || !result.tokenCount) {
    console.warn(`Failed to calculate actual prompt tokens: ${result.error ?? "No token count returned"}`);
    return FALLBACK_PROMPT_TOKENS;
  }

  console.log("Actual prompt tokens:", result.tokenCount);
  return result.tokenCount;
};

interface EstimateTokenParams {
  totalTokensWithoutQuery: number;
  contextDataLength: number;
  queryTokens?: number;
}

export const estimateSingleContextTokenLength = (params: EstimateTokenParams): number => {
  const { totalTokensWithoutQuery, contextDataLength, queryTokens = EMPTY_COUNT } = params;

  const contextOnlyTokens = Math.max(EMPTY_COUNT, totalTokensWithoutQuery - queryTokens);
  const averageTokensPerContext = contextOnlyTokens / contextDataLength;

  return Math.max(averageTokensPerContext, MIN_TOKENS_PER_CONTEXT);
};

type SetState = (partial: Partial<ChatState> | ((state: ChatState & ChatActions) => Partial<ChatState>)) => void;

interface CreateChunksParams {
  contextData: ContextDataItem[];
  totalCalculatedTokens: number;
  singleContextTokenLength: number;
  model?: AvailableModel;
  query?: string;
  abortSignal?: AbortSignal;
  set: SetState;
}

export const createChunks = async (params: CreateChunksParams): Promise<DataChunk[]> => {
  clearAllSavedExtractedData();

  const systemPrompt = getEnlightenMeSystemPrompt();
  const queryTokens = estimateQueryTokens(params.query ?? "");
  const actualPromptTokens = await calculateActualPromptTokens({
    systemPrompt,
    model: params.model,
    abortSignal: params.abortSignal
  });

  const adjustedSingleContextLength = estimateSingleContextTokenLength({
    totalTokensWithoutQuery: params.totalCalculatedTokens,
    contextDataLength: params.contextData.length,
    queryTokens
  });

  params.set({ singleContextTokenLength: adjustedSingleContextLength });

  const result = await chunkDataByTokens({
    data: params.contextData,
    totalCalculatedTokens: params.totalCalculatedTokens,
    singleContextTokenLength: adjustedSingleContextLength,
    maxInputTokens: config.maxInputTokens,
    actualPromptTokens,
    systemPrompt,
    model: params.model,
    query: params.query,
    abortSignal: params.abortSignal
  });

  const chunks: DataChunk[] = result.map((chunk) => ({
    data: chunk.data,
    startIndex: chunk.startIndex,
    endIndex: chunk.endIndex,
    estimatedTokens: chunk.actualTokens ?? chunk.estimatedTokens
  }));

  params.set({ contextChunks: chunks });

  return chunks;
};
